import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Image, Video, Search, Download, GitCompare, CheckCircle2, XCircle, Loader2, X, Trophy } from 'lucide-react';

export default function GalleryPage() {
  const [gens, setGens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [total, setTotal] = useState(0);

  // A/B Compare
  const [compareMode, setCompareMode] = useState(false);
  const [compareSelection, setCompareSelection] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [showCompare, setShowCompare] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (searchQuery) {
        const res = await api.searchGenerations({ q: searchQuery, gen_type: typeFilter || undefined, status: statusFilter || undefined, limit: 50 });
        setGens(res.results);
        setTotal(res.total);
      } else {
        const params = { limit: 50 };
        if (typeFilter) params.gen_type = typeFilter;
        if (statusFilter) params.status = statusFilter;
        const data = await api.listGenerations(params);
        setGens(data);
        setTotal(data.length);
      }
    } catch {} finally { setLoading(false); }
  }, [searchQuery, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const loadComps = async () => {
      try { setComparisons(await api.listComparisons()); } catch {}
    };
    loadComps();
  }, []);

  const handleDownload = (gen) => {
    if (!gen.output_url) return;
    const a = document.createElement('a');
    a.href = gen.output_url;
    a.download = `thehuman-${gen.id.slice(0, 8)}.${gen.gen_type === 'video' ? 'mp4' : 'png'}`;
    a.target = '_blank';
    a.click();
    toast.success('Download started');
  };

  const toggleCompareSelect = (gen) => {
    if (compareSelection.find(g => g.id === gen.id)) {
      setCompareSelection(prev => prev.filter(g => g.id !== gen.id));
    } else if (compareSelection.length < 4) {
      setCompareSelection(prev => [...prev, gen]);
    } else {
      toast.error('Max 4 items for comparison');
    }
  };

  const createComparison = async () => {
    if (compareSelection.length < 2) { toast.error('Select at least 2 generations'); return; }
    try {
      const comp = await api.createComparison({ generation_ids: compareSelection.map(g => g.id) });
      toast.success('Comparison created!');
      setCompareMode(false);
      setCompareSelection([]);
      setComparisons(await api.listComparisons());
      setShowCompare(comp);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const statusIcon = (s) => {
    if (s === 'completed') return <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />;
    if (s === 'failed') return <XCircle className="w-3.5 h-3.5 text-red-400" />;
    if (s === 'processing') return <Loader2 className="w-3.5 h-3.5 text-yellow-400 animate-spin" />;
    return null;
  };

  return (
    <div className="animate-fade-in-up opacity-0" data-testid="gallery-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: 'Chivo' }}>Gallery</h1>
          <p className="text-white/40 mt-1">{total} generation{total !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setCompareMode(!compareMode); setCompareSelection([]); }} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all ${compareMode ? 'bg-[#7C3AED] text-white' : 'bg-white/5 text-white/40 hover:text-white'}`} data-testid="compare-mode-btn">
          <GitCompare className="w-4 h-4" /> {compareMode ? 'Cancel Compare' : 'A/B Compare'}
        </button>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap gap-3 mb-6" data-testid="gallery-filters">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="forge-input w-full pl-10" placeholder="Search prompts, templates..." data-testid="search-input" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="forge-input text-sm h-12 px-3 min-w-[120px]" data-testid="type-filter">
          <option value="">All Types</option>
          <option value="image">Images</option>
          <option value="video">Videos</option>
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="forge-input text-sm h-12 px-3 min-w-[120px]" data-testid="status-filter">
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="processing">Processing</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Compare Bar */}
      {compareMode && (
        <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-xl p-4 mb-6 flex items-center justify-between" data-testid="compare-bar">
          <p className="text-sm text-[#7C3AED]">
            Select 2-4 generations to compare. <strong>{compareSelection.length}</strong> selected.
          </p>
          <button onClick={createComparison} disabled={compareSelection.length < 2} className="btn-primary text-sm py-2 px-4" data-testid="create-comparison-btn">
            Compare ({compareSelection.length})
          </button>
        </div>
      )}

      {/* Previous Comparisons */}
      {comparisons.length > 0 && !compareMode && (
        <div className="mb-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-white/30 mb-3">Previous Comparisons</h3>
          <div className="flex flex-wrap gap-2">
            {comparisons.slice(0, 5).map(c => (
              <button key={c.id} onClick={() => setShowCompare(c)} className="text-xs px-3 py-1.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded-full hover:bg-[#7C3AED]/20 transition-colors flex items-center gap-1.5" data-testid={`comparison-${c.id}`}>
                <GitCompare className="w-3 h-3" /> {c.name} ({c.generation_ids?.length || 0})
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#CDFE65] border-t-transparent rounded-full animate-spin" /></div>
      ) : gens.length === 0 ? (
        <div className="bg-[#0F0F11] border border-white/5 rounded-xl p-16 text-center">
          <Image className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40">{searchQuery ? 'No results found' : 'No generations yet'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {gens.map(gen => {
            const isSelected = compareSelection.find(g => g.id === gen.id);
            return (
              <div key={gen.id}
                className={`relative group overflow-hidden rounded-xl border card-hover cursor-pointer ${isSelected ? 'border-[#7C3AED] ring-2 ring-[#7C3AED]/30' : 'border-white/5'}`}
                onClick={() => compareMode ? toggleCompareSelect(gen) : setSelected(gen)}
                data-testid={`gallery-gen-${gen.id}`}
              >
                {gen.output_url ? (
                  <img src={gen.output_url} alt={gen.prompt || ''} className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                ) : (
                  <div className="w-full aspect-square bg-[#18181B] flex items-center justify-center">
                    {gen.status === 'processing' ? <Loader2 className="w-6 h-6 text-[#CDFE65] animate-spin" /> : <XCircle className="w-6 h-6 text-white/10" />}
                  </div>
                )}
                {compareMode && isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-[#7C3AED] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {compareSelection.findIndex(g => g.id === gen.id) + 1}
                  </div>
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                  {gen.gen_type === 'video' ? <Video className="w-3.5 h-3.5 text-white/60" /> : <Image className="w-3.5 h-3.5 text-white/60" />}
                </div>
                <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {statusIcon(gen.status)}
                      <span className="text-[10px] font-mono text-white/60">{gen.model_id}</span>
                    </div>
                    {gen.output_url && !compareMode && (
                      <button onClick={(e) => { e.stopPropagation(); handleDownload(gen); }} className="text-white/40 hover:text-[#CDFE65] transition-colors" data-testid={`download-${gen.id}`}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox */}
      {selected && !compareMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setSelected(null)} data-testid="gallery-lightbox">
          <div className="max-w-3xl w-full mx-4 bg-[#0F0F11] border border-white/10 rounded-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            {selected.output_url && <img src={selected.output_url} alt="" className="w-full max-h-[60vh] object-contain bg-black" />}
            <div className="p-5 space-y-3">
              {selected.prompt && <p className="text-sm text-white/60">{selected.prompt}</p>}
              {selected.enhanced_prompt && <p className="text-xs text-white/30 font-mono">{selected.enhanced_prompt}</p>}
              <div className="flex flex-wrap gap-2 text-xs font-mono">
                <span className="px-2 py-1 bg-white/5 rounded">{selected.gen_type}</span>
                <span className="px-2 py-1 bg-white/5 rounded">{selected.model_id}</span>
                <span className="px-2 py-1 bg-white/5 rounded">{selected.status}</span>
                {selected.generation_time_ms && <span className="px-2 py-1 bg-white/5 rounded">{selected.generation_time_ms}ms</span>}
                {selected.cost_usd > 0 && <span className="px-2 py-1 bg-white/5 rounded">${selected.cost_usd.toFixed(3)}</span>}
                {selected.template_id && <span className="px-2 py-1 bg-white/5 rounded">{selected.template_id}</span>}
              </div>
              <div className="flex gap-2 pt-2">
                {selected.output_url && (
                  <button onClick={() => handleDownload(selected)} className="btn-primary text-sm flex items-center gap-2 py-2" data-testid="lightbox-download">
                    <Download className="w-4 h-4" /> Download
                  </button>
                )}
                <button onClick={() => setSelected(null)} className="btn-secondary text-sm py-2">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* A/B Comparison View */}
      {showCompare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={() => setShowCompare(null)} data-testid="comparison-modal">
          <div className="max-w-5xl w-full mx-4 bg-[#0F0F11] border border-white/10 rounded-2xl p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <GitCompare className="w-5 h-5 text-[#7C3AED]" />
                <h2 className="text-xl font-bold">{showCompare.name}</h2>
              </div>
              <button onClick={() => setShowCompare(null)} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className={`grid gap-4 ${(showCompare.generations?.length || 0) <= 2 ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-3'}`}>
              {(showCompare.generations || []).map(gen => (
                <div key={gen.id} className={`rounded-xl border overflow-hidden ${showCompare.winner_id === gen.id ? 'border-[#CDFE65] ring-2 ring-[#CDFE65]/30' : 'border-white/5'}`}>
                  {gen.output_url ? (
                    <img src={gen.output_url} alt="" className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-[#18181B] flex items-center justify-center"><XCircle className="w-8 h-8 text-white/10" /></div>
                  )}
                  <div className="p-3 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-white/40">{gen.model_id}</span>
                      {showCompare.winner_id === gen.id && <Trophy className="w-4 h-4 text-[#CDFE65]" />}
                    </div>
                    <p className="text-[10px] text-white/30 line-clamp-2">{gen.prompt}</p>
                    <div className="flex gap-1 text-[10px] font-mono text-white/20">
                      <span>{gen.generation_time_ms}ms</span>
                      <span>·</span>
                      <span>${gen.cost_usd?.toFixed(3)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
